import React, { useState, useEffect, useRef } from 'react';
import { IconLogo } from './Icons.jsx';

/**
 * InstallPrompt - Full-screen overlay that prompts the user to install the PWA.
 *
 * Android: uses beforeinstallprompt API for one-tap native install.
 * iOS: downloads a .mobileconfig profile that adds a Web Clip to the home screen.
 *
 * Only shows if:
 * - User is NOT already in standalone mode (already installed)
 * - User hasn't dismissed it in the last 7 days
 */

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isAndroid() {
  return /Android/.test(navigator.userAgent);
}

function wasDismissed() {
  try {
    const ts = localStorage.getItem('aigo_install_dismissed');
    if (!ts) return false;
    const days = (Date.now() - parseInt(ts, 10)) / (1000 * 60 * 60 * 24);
    return days < 7; // Don't show again for 7 days after dismiss
  } catch { return false; }
}

export default function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState('unknown'); // ios | android | desktop
  const [installing, setInstalling] = useState(false);
  const [step, setStep] = useState('prompt'); // prompt | ios-instructions
  const deferredPromptRef = useRef(null);

  useEffect(() => {
    // Don't show if already installed or recently dismissed
    if (isStandalone() || wasDismissed()) return;

    if (isIOS()) {
      setPlatform('ios');
      setShow(true);
    } else if (isAndroid()) {
      setPlatform('android');
      // Listen for the native install prompt
      const handler = (e) => {
        e.preventDefault();
        deferredPromptRef.current = e;
        setShow(true);
      };
      window.addEventListener('beforeinstallprompt', handler);
      // If no prompt fires in 3s, show iOS-style fallback
      const timeout = setTimeout(() => {
        if (!deferredPromptRef.current) setShow(true);
      }, 3000);
      return () => {
        window.removeEventListener('beforeinstallprompt', handler);
        clearTimeout(timeout);
      };
    }
    // Desktop: don't show install prompt
  }, []);

  const handleDismiss = () => {
    try { localStorage.setItem('aigo_install_dismissed', String(Date.now())); } catch {}
    setShow(false);
  };

  const handleAndroidInstall = async () => {
    if (!deferredPromptRef.current) return;
    setInstalling(true);
    try {
      deferredPromptRef.current.prompt();
      const result = await deferredPromptRef.current.userChoice;
      if (result.outcome === 'accepted') {
        setShow(false);
      }
    } catch {}
    setInstalling(false);
  };

  const handleIOSInstall = () => {
    // Download the .mobileconfig profile
    window.location.href = '/install-profile';
    setStep('ios-instructions');
  };

  if (!show) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <IconLogo size={48} style={{ color: '#0C7EFF' }} />

        {step === 'prompt' && (
          <>
            <h2 style={styles.title}>Install AiGO</h2>
            <p style={styles.subtitle}>
              Add AiGO to your home screen for instant access. 
              Opens full-screen like a native app.
            </p>

            {platform === 'android' && deferredPromptRef.current && (
              <button style={styles.installBtn} onClick={handleAndroidInstall} disabled={installing}>
                {installing ? 'Installing...' : 'Install App'}
              </button>
            )}

            {platform === 'ios' && (
              <button style={styles.installBtn} onClick={handleIOSInstall}>
                Install App
              </button>
            )}

            {/* Android fallback if beforeinstallprompt didn't fire */}
            {platform === 'android' && !deferredPromptRef.current && (
              <button style={styles.installBtn} onClick={handleIOSInstall}>
                Install App
              </button>
            )}

            <button style={styles.dismissBtn} onClick={handleDismiss}>
              Not now
            </button>
          </>
        )}

        {step === 'ios-instructions' && (
          <>
            <h2 style={styles.title}>Almost there!</h2>
            <div style={styles.steps}>
              <div style={styles.stepItem}>
                <div style={styles.stepNumber}>1</div>
                <div style={styles.stepText}>
                  Tap <strong>"Allow"</strong> when prompted to download the profile
                </div>
              </div>
              <div style={styles.stepItem}>
                <div style={styles.stepNumber}>2</div>
                <div style={styles.stepText}>
                  Open <strong>Settings</strong> (a notification will appear)
                </div>
              </div>
              <div style={styles.stepItem}>
                <div style={styles.stepNumber}>3</div>
                <div style={styles.stepText}>
                  Tap <strong>"Install"</strong> on the AiGO profile
                </div>
              </div>
              <div style={styles.stepItem}>
                <div style={styles.stepNumber}>4</div>
                <div style={styles.stepText}>
                  AiGO appears on your home screen. Done!
                </div>
              </div>
            </div>
            <button style={styles.dismissBtn} onClick={handleDismiss}>
              Got it
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    background: 'rgba(0, 0, 0, 0.85)',
    backdropFilter: 'blur(12px)',
  },
  card: {
    width: '100%',
    maxWidth: 340,
    background: '#0D1120',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 16,
    padding: '32px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    textAlign: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: -0.5,
    color: '#fff',
    margin: 0,
  },
  subtitle: {
    fontSize: 15,
    color: '#7A8499',
    lineHeight: 1.5,
    margin: 0,
  },
  installBtn: {
    width: '100%',
    padding: '14px 28px',
    background: '#0C7EFF',
    color: '#070A12',
    fontSize: 17,
    fontWeight: 700,
    border: 'none',
    borderRadius: 12,
    cursor: 'pointer',
    marginTop: 8,
  },
  dismissBtn: {
    background: 'none',
    border: 'none',
    color: '#4A5470',
    fontSize: 14,
    cursor: 'pointer',
    padding: '8px 16px',
  },
  steps: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    width: '100%',
    textAlign: 'left',
  },
  stepItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
  },
  stepNumber: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: 'rgba(12, 126, 255, 0.12)',
    color: '#0C7EFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 700,
    flexShrink: 0,
  },
  stepText: {
    fontSize: 14,
    color: '#E8EEFF',
    lineHeight: 1.5,
    paddingTop: 3,
  },
};
